// FWDCO Group. I18n dictionaries (English + Spanish)
// URL prefix convention: "/" = en, "/es" = es

export const languages = {
  en: { code: "en", label: "EN", htmlLang: "en", locale: "en_US" },
  es: { code: "es", label: "ES", htmlLang: "es", locale: "es_419" },
};

export const translations = {
  en: {
    meta: {
      title: "FWDCO Group | Strategic counsel for leaders building across the Americas",
      description:
        "Boutique advisory for C-level executives and founders operating between the United States and Latin America. Experience first. Technology always.",
    },
    header: {
      navOurPractice: "Our Practice",
      navServices: "Services",
      navPartners: "Partners",
      navProcess: "Process",
      navLearnMore: "Learn More",
      cta: "Request Assessment",
    },
    hero: {
      eyebrow: "Boutique Advisory · United States & Latin America",
      headlineLead: "Strategic counsel for leaders building",
      headlineItalic: "across the Americas.",
      body:
        "FWDCO Group advises C-level executives and founders operating between the United States and Latin America. Senior judgment for the decisions that define your company. And the structure to make them hold.",
      cta: "Request Assessment",
      scroll: "Scroll",
    },
    marquee: [
      "Strategic Counsel", "Cross-Border Advisory", "Senior Judgment",
      "Cross-Border Execution", "C-Level Advisory", "AI-First",
      "Discreet · Long-Term", "Experience First. Technology Always.",
    ],
    howWeWork: {
      eyebrow: "How We Work",
      headline: "Most advisory firms fix one thing.\nThen they leave.",
      paragraph1:
        "The company, the team, the leader, the product. Most firms treat them in isolation. Six months later, the same problems are back. Not because the advice was wrong, but because nothing was connected.",
      paragraph2:
        "Every business operates across four fronts at once. Each one affects the others.",
      cards: [
        {
          number: "01",
          title: "COMPANY",
          description:
            "Revenue, structure, market position and strategic direction. The architecture that everything else is built on.",
        },
        {
          number: "02",
          title: "PRODUCT",
          description:
            "Relevance, evolution and product-market fit. What you offer has to grow as fast as the market moves.",
        },
        {
          number: "03",
          title: "TEAM",
          description:
            "Capacity, culture, performance and the ability to execute without losing the people who make execution possible.",
        },
        {
          number: "04",
          title: "LEADER",
          description:
            "Clarity, decision-making under pressure and the vision to know where the business needs to go before the market forces your hand.",
        },
      ],
      closing1: "What we build with you\nhas to work without us.",
      closing2:
        "That means results your team can execute with precision, communicate with clarity, and sustain long after the engagement ends.",
      closing3:
        "Structure that holds.\nNot because we're still there.\nBecause it was built to last.",
    },
    founder: {
      eyebrow: "From The Founder",
      name: "Juan Roberto Martinez",
      role: "Founder · FWDCO Group",
      credentials:
        "Founder of Central America's first electric mobility ecosystem. A decade of operating experience between North American capital and Latin American markets.",
      linkedinUrl: "https://www.linkedin.com/in/juanmartinezcalderon/",
      linkedinLabel: "Connect on LinkedIn",
      quoteLead: "I built FWDCO because I needed it",
      quoteItalic: "and it didn't exist.",
      paragraphs: [
        "There is a particular solitude at the top of a company. The decisions that should never be made alone get made alone. Because the people who could genuinely help either have something to sell you, or have never sat where you sit.",
        "I know that seat. I founded and operated the first electric mobility ecosystem in Central America: raising capital, building teams across borders, and learning which decisions survive contact with reality. The counsel that changed my trajectory never came from frameworks or certifications. It came from people who had built, pivoted, and endured.",
        "FWDCO Group is that counsel, made into a firm. So the leader sitting where I once sat never has to navigate alone.",
        "One more thing. What we build together stays confidential, always. Your name, your company, and every conversation we have will never become a case study. We believe in discreet, long-term relationships. Not marketing material.",
      ],
      signatureMotto: "Experience first. Technology always.",
    },
    pricing: {
      eyebrow: "What We Offer",
      headline1: "Advisory built",
      headline2: "on",
      headlineItalic: "real stakes.",
      intro:
        "Four engagement tiers, each designed for a different stage of organizational complexity. Every client works directly with the founding partner. No junior associates, no templated frameworks.",
      tierLabel: (roman) => `Tier ${roman}`,
      signatureBadge: "Signature Engagement",
      footnote:
        "Engagement terms and investment are defined during the assessment. Calibrated to scope, stage, and stakes.",
      tiers: [
        {
          tier: "Discovery",
          name: "Discovery",
          description:
            "The entry point. For leaders who want to experience focused, senior counsel before committing to a deeper engagement.",
          features: [
            "2 × 60-min strategy sessions / month",
            "Initial business diagnostic",
            "Strategic priority mapping",
            "Async WhatsApp access",
          ],
          footer: "Monthly · Limited Availability",
        },
        {
          tier: "I",
          name: "Strategic Retainer",
          description:
            "For emerging executives and SME owners who need a trusted strategic sounding board. Direct access, no intermediaries.",
          features: [
            "4 × 60-min strategy sessions / month",
            "2 written strategic recommendations",
            "Direct Office Hours WhatsApp access",
            "Monthly business health review",
          ],
          footer: "Monthly Engagement",
        },
        {
          tier: "II",
          name: "Executive Advisory",
          description:
            "For C-level executives navigating growth, transformation, or competitive disruption. An embedded strategic partnership, enhanced by AI.",
          features: [
            "6 × strategy sessions / month",
            "Weekly written intelligence briefings",
            "AI implementation roadmap",
            "Priority response within 4 hours",
            "Quarterly board-ready summary",
            "Access to expert contractor network",
          ],
          footer: "Monthly Engagement",
        },
        {
          tier: "III",
          name: "Boardroom Partner",
          description:
            "Full strategic partnership, embedded in your leadership team. Reserved for organizations committed to serious transformation.",
          features: [
            "Unlimited sessions",
            "Board presentation support",
            "Full AI transformation advisory",
            "Complete expert network access",
            "NDA executed on day one",
            "Outcome-linked fee structures available",
          ],
          signature: true,
          footer: "By Application Only",
        },
      ],
      projectEngagement: {
        eyebrow: "Special Engagements",
        name: "Project Engagements",
        description:
          "Business model audits and strategic repositioning for companies at inflection points. AI transformation projects, fundraising preparation, and pivot strategy engagements.",
        price: "Scoped individually. Application required.",
      },
    },
    ai: {
      eyebrow: "The Difference",
      headline1: "AI-Native.",
      headline2: "Not AI-",
      headlineItalic: "Dependent.",
      intro:
        "We don't use artificial intelligence to replace judgment. We use it so your team can make better decisions faster, with less friction.",
      features: [
        {
          title: "Real-Time Market Intelligence",
          description:
            "Every engagement includes market analysis, competitive benchmarking, and trend synthesis executed with specialized AI tools. Your team receives actionable intelligence. Not 80-page reports nobody reads.",
        },
        {
          title: "Systems Your Team Actually Uses",
          description:
            "The right tool implementation is a curated process: we evaluate what makes sense for your industry, your team, and your specific moment. And integrate only what generates real results, not what's trending.",
        },
        {
          title: "Human Judgment. Always.",
          description:
            "AI amplifies our thinking, it never replaces it. Every recommendation that reaches your desk has been filtered through years of real operational experience, tested by leaders in global industries. Something no model can replicate.",
        },
      ],
      badge: "Intelligence Architecture",
      badgeTag: "AI · Human · Strategy",
    },
    network: {
      eyebrow: "The Network",
      headline1: "One relationship.",
      headlineItalic: "Every expertise.",
      intro:
        "Behind FWDCO Group is a curated network of senior specialists. Deployed on demand, coordinated by the founding partner, accountable to your outcomes.",
      coreOfferingLabel: "Core Offering",
      coreOfferingTitle: "Strategic Decision & Direction",
      coreOfferingDesc:
        "The decisions that define your year should never be made alone, in emotion, or without context. We model every critical decision on knowledge, market experience, and real-time market stage. So you move forward with certainty, not hope.",
      specialists: [
        { label: "Operations", description: "Process design, supply chain optimization, and operational scalability advisory." },
        { label: "Technology & AI", description: "CTO-level guidance on architecture, AI implementation, and digital transformation." },
        { label: "Sales & Revenue", description: "Go-to-market strategy, sales system design, and revenue acceleration for LatAm markets." },
        { label: "Finance & Accounting", description: "CFO-level financial strategy, structuring, and compliance for growth-stage companies." },
      ],
    },
    globe: {
      eyebrow: "Bi-Continental Strategists",
      headlineLead: "Strategists fluent in the American market",
      headlineItalic: "and Latin American culture.",
      subtitle:
        "Real presence on both sides of the continent. We support you, your company, and your project. Comprehensively, in either direction.",
      locations: ["Dallas", "US Latino Market", "México", "Panamá", "Colombia", "Brasil", "Chile", "Argentina"],
      blocks: [
        {
          title: "United States",
          image: "/Dallas_Skyline.jpg",
          imagePosition: "center",
          description:
            "We work with American companies operating teams, resources, or vendors across Latin America. Building systems that run smoothly across both cultures, without operational friction. We also guide North American firms entering Latin American markets with precision.",
        },
        {
          title: "Latin America",
          image: "/Panama_Skyline.jpg",
          imagePosition: "center 35%",
          description:
            "We partner with founders and executives across LatAm who are ready to expand into the United States. In fintech, energy tech, blockchain, digital marketing, and beyond. Leaders who need more than a lawyer or an accountant: a strategic partner who knows the path, because we've walked it.",
        },
      ],
    },
    process: {
      eyebrow: "How It Works",
      headline1: "A process built",
      headline2: "on",
      headlineItalic: "respect.",
      stepLabel: (n) => `Step ${n}`,
      steps: [
        {
          title: "Confidential Inquiry",
          description:
            "You submit a brief inquiry describing your organization and the challenge you're navigating. No forms. No automated sequences. Reviewed personally within 48 hours.",
        },
        {
          title: "Partner Assessment",
          description:
            "A senior partner conducts a 60-minute diagnostic call. We assess fit, scope, and urgency. We decline engagements we cannot materially impact. This protects your time and our reputation.",
        },
        {
          title: "Engagement Design",
          description:
            "If mutual fit exists, we present a bespoke engagement proposal. Specific outcomes, methodology, timeline, and investment. No templated decks.",
        },
        {
          title: "Intensive Engagement",
          description:
            "Work begins immediately. Your lead partner is embedded with your leadership team, physically or virtually, for the duration of the mandate.",
        },
      ],
    },
    finalCTA: {
      eyebrow: "Begin the Conversation",
      headline1: "The right advisor",
      headline2: "changes",
      headlineItalic: "everything.",
      paragraph:
        "FWDCO Group accepts a limited number of new clients each quarter. Whether you're expanding into the US or strengthening your operations in LatAm. If you're navigating a critical inflection point, we want to hear from you.",
      cta: "Request Assessment",
    },
    contact: {
      eyebrow: "Submit Your Inquiry",
      headline: "Request Assessment",
      subtitle:
        "Reviewed personally by the founding partner. Response within 48 hours.",
      highlights: [
        "NDA executed before any strategic briefing",
        "No junior associates. Founding partner only",
        "Personal response within 48 hours",
        "Outcome-linked fee structures available",
        "No noise. No templated frameworks.",
      ],
      form: {
        name: "Full Name",
        email: "Email Address",
        organization: "Organization",
        revenue: "Your organization's revenue",
        challenge:
          "Primary challenge (please share at least a sentence. Minimum 10 characters)",
        submit: "Request Assessment",
        revenueRanges: [
          { value: "under_1m", label: "Under $1M" },
          { value: "1m_5m", label: "$1M – $5M" },
          { value: "5m_25m", label: "$5M – $25M" },
          { value: "25m_100m", label: "$25M – $100M" },
          { value: "100m_plus", label: "$100M+" },
        ],
      },
      errors: {
        required: "Please fill in all fields",
        shortName: "Please enter your full name (at least 2 characters).",
        shortOrg: "Please enter your organization name (at least 2 characters).",
        shortChallenge:
          "Please describe your challenge in a bit more detail (at least 10 characters).",
        generic: "Something went wrong. Please try again.",
        fieldError: (field, msg) => `Please check the "${field}" field: ${msg}.`,
      },
      success:
        "Your inquiry has been submitted. We'll respond within 48 hours.",
      receivedTitle: "Inquiry Received",
      receivedDesc:
        "Reviewed personally by the founding partner. Response within 48 hours.",
      submitAnother: "Submit Another Inquiry",
      directEmailLead: "Or write to us directly at",
      directEmail: "info@fwdcogroup.com",
      directEmailTail:
        ". We respond in under 48 hours. No forms, no automation.",
    },
    footer: {
      tagline: "Strategic counsel for leaders building across the Americas.",
      links: [
        { label: "Our Practice", href: "#about" },
        { label: "Services", href: "#pricing" },
        { label: "Partners", href: "#network" },
        { label: "Process", href: "#process" },
        { label: "Contact", href: "#contact" },
      ],
      copyright: (year) => `© ${year} FWDCO Group`,
    },
    newsletter: {
      title: "Curated Intelligence. Not For Everyone.",
      description:
        "Our newsletter is not open. We review every request and select those we believe will find real value. Resources, frameworks, and analysis we don't publish anywhere else.",
      emailPlaceholder: "Your professional email",
      submit: "Request Access",
      submitting: "Sending…",
      success: "Request received. If there's a fit, we'll reach out.",
      successTitle: "Request Received",
      successDesc:
        "We review every request. If there's a fit, we'll reach out within 48 hours.",
      close: "Close",
      maybeLater: "We review every request. If there's a fit, we'll reach out.",
      invalidEmail: "Please enter a valid email address.",
      genericError: "Something went wrong. Please try again.",
    },
  },

  // ================================================================
  // SPANISH
  // ================================================================
  es: {
    meta: {
      title:
        "FWDCO Group | Consejo estratégico para líderes que construyen a través de las Américas",
      description:
        "Advisory boutique para ejecutivos C-level y founders que operan entre Estados Unidos y Latinoamérica. Experiencia primero. Tecnología siempre.",
    },
    header: {
      navOurPractice: "Nuestra Práctica",
      navServices: "Servicios",
      navPartners: "Socios",
      navProcess: "Proceso",
      navLearnMore: "Conoce Más",
      cta: "Solicitar Assessment",
    },
    hero: {
      eyebrow: "Advisory Boutique · Estados Unidos y Latinoamérica",
      headlineLead: "Consejo estratégico para líderes que construyen",
      headlineItalic: "a través de las Américas.",
      body:
        "FWDCO Group asesora a ejecutivos C-level y founders que operan entre Estados Unidos y Latinoamérica. Criterio senior para las decisiones que definen su empresa. Y la estructura para que se sostengan.",
      cta: "Solicitar Assessment",
      scroll: "Desliza",
    },
    marquee: [
      "Consejo Estratégico", "Advisory Cross-Border", "Criterio Senior",
      "Ejecución Cross-Border", "Asesoría C-Level", "AI-First",
      "Discreto · Largo Plazo", "Experiencia primero. Tecnología siempre.",
    ],
    howWeWork: {
      eyebrow: "Cómo Trabajamos",
      headline: "La mayoría de las firmas arreglan una cosa.\nY se van.",
      paragraph1:
        "La empresa, el equipo, el líder, el producto. La mayoría de las firmas los trata por separado. Seis meses después, los mismos problemas regresan. No porque el consejo estuviera mal, sino porque nada estaba conectado.",
      paragraph2:
        "Todo negocio opera en cuatro frentes a la vez. Y cada uno afecta a los demás.",
      cards: [
        {
          number: "01",
          title: "EMPRESA",
          description:
            "Ingresos, estructura, posición de mercado y dirección estratégica. La arquitectura sobre la que todo lo demás se construye.",
        },
        {
          number: "02",
          title: "PRODUCTO",
          description:
            "Relevancia, evolución y product-market fit. Lo que ofreces tiene que crecer tan rápido como se mueve el mercado.",
        },
        {
          number: "03",
          title: "EQUIPO",
          description:
            "Capacidad, cultura, desempeño y la habilidad de ejecutar sin perder a las personas que hacen posible la ejecución.",
        },
        {
          number: "04",
          title: "LÍDER",
          description:
            "Claridad, toma de decisiones bajo presión y la visión de saber hacia dónde necesita ir el negocio antes de que el mercado te fuerce la mano.",
        },
      ],
      closing1: "Lo que construimos contigo\ntiene que funcionar sin nosotros.",
      closing2:
        "Eso significa resultados que tu equipo pueda ejecutar con precisión, comunicar con claridad, y sostener mucho después de que el engagement termine.",
      closing3:
        "Estructura que perdura.\nNo porque sigamos ahí.\nSino porque fue construida para durar.",
    },
    founder: {
      eyebrow: "Del Fundador",
      name: "Juan Roberto Martinez",
      role: "Fundador · FWDCO Group",
      credentials:
        "Fundador del primer ecosistema de movilidad eléctrica de Centroamérica. Una década de experiencia operativa entre el capital norteamericano y los mercados latinoamericanos.",
      linkedinUrl: "https://www.linkedin.com/in/juanmartinezcalderon/",
      linkedinLabel: "Conecta en LinkedIn",
      quoteLead: "Construí FWDCO porque lo necesitaba",
      quoteItalic: "y no existía.",
      paragraphs: [
        "Existe una soledad particular en la cima de una empresa. Las decisiones que nunca deberían tomarse solo, se toman solo. Porque quienes podrían ayudar de verdad, o tienen algo que venderte, o nunca se han sentado donde tú estás sentado.",
        "Conozco esa silla. Fundé y operé el primer ecosistema de movilidad eléctrica de Centroamérica: levantando capital, construyendo equipos entre fronteras, y aprendiendo qué decisiones sobreviven al contacto con la realidad. El consejo que cambió mi trayectoria nunca vino de frameworks ni certificaciones. Vino de personas que habían construido, pivotado y resistido.",
        "FWDCO Group es ese consejo, convertido en firma. Para que el líder sentado donde yo estuve nunca tenga que navegarlo solo.",
        "Una cosa más. Lo que construimos juntos es confidencial, siempre. Tu nombre, tu empresa y cada conversación que tengamos jamás se convertirán en un caso de estudio. Creemos en relaciones discretas y de largo plazo. No en material de marketing.",
      ],
      signatureMotto: "Experience first. Technology always.",
    },
    pricing: {
      eyebrow: "Qué Ofrecemos",
      headline1: "Asesoría con",
      headline2: "consecuencias",
      headlineItalic: "reales.",
      intro:
        "Cuatro niveles de engagement, cada uno diseñado para una etapa distinta de complejidad organizacional. Cada cliente trabaja directamente con el founding partner. Sin asociados junior, sin frameworks de plantilla.",
      tierLabel: (roman) => `Nivel ${roman}`,
      signatureBadge: "Engagement Signature",
      footnote:
        "Los términos e inversión de cada engagement se definen durante el assessment. Calibrados al alcance, la etapa y lo que está en juego.",
      tiers: [
        {
          tier: "Discovery",
          name: "Discovery",
          description:
            "El punto de entrada. Para líderes que quieren experimentar consejo senior y enfocado antes de comprometerse con un engagement más profundo.",
          features: [
            "2 × sesiones de 60 min / mes",
            "Diagnóstico inicial del negocio",
            "Mapeo de prioridades estratégicas",
            "Acceso asíncrono por WhatsApp",
          ],
          footer: "Mensual · Disponibilidad Limitada",
        },
        {
          tier: "I",
          name: "Strategic Retainer",
          description:
            "Para ejecutivos emergentes y dueños de PYMEs que necesitan un sounding board estratégico de confianza. Acceso directo, sin intermediarios.",
          features: [
            "4 × sesiones de 60 min / mes",
            "2 recomendaciones estratégicas por escrito",
            "Acceso WhatsApp en horario de oficina",
            "Revisión mensual de salud del negocio",
          ],
          footer: "Engagement Mensual",
        },
        {
          tier: "II",
          name: "Executive Advisory",
          description:
            "Para ejecutivos C-level navegando crecimiento, transformación o disrupción competitiva. Una alianza estratégica embebida, potenciada por IA.",
          features: [
            "6 × sesiones estratégicas / mes",
            "Briefings de inteligencia semanales",
            "Roadmap de implementación de IA",
            "Respuesta prioritaria en 4 horas",
            "Resumen trimestral listo para board",
            "Acceso a red de expertos contratistas",
          ],
          footer: "Engagement Mensual",
        },
        {
          tier: "III",
          name: "Boardroom Partner",
          description:
            "Alianza estratégica total, embebida en tu equipo de liderazgo. Reservada para organizaciones comprometidas con una transformación seria.",
          features: [
            "Sesiones ilimitadas",
            "Soporte en presentaciones al board",
            "Asesoría completa en transformación con IA",
            "Acceso total a la red de expertos",
            "NDA firmado el primer día",
            "Estructuras de fee vinculadas a resultados",
          ],
          signature: true,
          footer: "Solo Por Aplicación",
        },
      ],
      projectEngagement: {
        eyebrow: "Engagements Especiales",
        name: "Engagements por Proyecto",
        description:
          "Auditorías de modelo de negocio y reposicionamientos estratégicos para empresas en puntos de inflexión. Proyectos de transformación con IA, preparación para levantamiento de capital y estrategias de pivot.",
        price: "Alcance definido individualmente. Requiere aplicación.",
      },
    },
    ai: {
      eyebrow: "La Diferencia",
      headline1: "AI-Native.",
      headline2: "No AI-",
      headlineItalic: "Dependent.",
      intro:
        "No usamos inteligencia artificial para reemplazar el criterio, la usamos para que tu equipo tome mejores decisiones más rápido, con menos fricción.",
      features: [
        {
          title: "Inteligencia de Mercado en Tiempo Real",
          description:
            "Cada engagement incluye análisis de mercado, benchmarking competitivo y síntesis de tendencias ejecutados con herramientas de IA especializadas. Tu equipo recibe inteligencia accionable, no reportes de 80 páginas que nadie lee.",
        },
        {
          title: "Sistemas que tu Equipo Realmente Usa",
          description:
            "La implementación correcta de herramientas es un proceso curado: evaluamos qué tiene sentido para tu industria, tu equipo y tu momento específico. E integramos solo lo que genera resultado real, no lo que está de moda.",
        },
        {
          title: "Criterio Humano. Siempre.",
          description:
            "La IA amplifica nuestro pensamiento, nunca lo reemplaza. Cada recomendación que llega a tu escritorio ha pasado por años de experiencia operacional real, y probados por líderes en industrias globales. Algo que ningún modelo puede replicar.",
        },
      ],
      badge: "Arquitectura de Inteligencia",
      badgeTag: "IA · Humano · Estrategia",
    },
    network: {
      eyebrow: "La Red",
      headline1: "Una relación.",
      headlineItalic: "Todas las especialidades.",
      intro:
        "Detrás de FWDCO Group hay una red curada de especialistas senior. Desplegados a demanda, coordinados por el socio fundador, responsables de tus resultados.",
      coreOfferingLabel: "Oferta Central",
      coreOfferingTitle: "Decisión y Dirección Estratégica",
      coreOfferingDesc:
        "Las decisiones que definen tu año nunca deberían tomarse en soledad, en caliente o sin contexto. Modelamos cada decisión crítica con conocimiento, experiencia de mercado y el estado real del mercado en tiempo real. Para que avances con certeza, no con esperanza.",
      specialists: [
        { label: "Operaciones", description: "Diseño de procesos, optimización de cadena de suministro y escalabilidad operativa." },
        { label: "Tecnología e IA", description: "Guía nivel CTO en arquitectura, implementación de IA y transformación digital." },
        { label: "Ventas e Ingresos", description: "Estrategia go-to-market, diseño de sistema de ventas y aceleración de ingresos para LatAm." },
        { label: "Finanzas y Contabilidad", description: "Estrategia financiera nivel CFO, estructuración y compliance para empresas en crecimiento." },
      ],
    },
    globe: {
      eyebrow: "Estrategas Bi-Continentales",
      headlineLead: "Estrategas que dominan el mercado americano",
      headlineItalic: "y la cultura latinoamericana.",
      subtitle:
        "Presencia real en ambos lados del continente. Apoyamos a tu empresa, tu proyecto y a ti. Integralmente, en cualquier dirección.",
      locations: ["Dallas", "Mercado Latino en EE.UU.", "México", "Panamá", "Colombia", "Brasil", "Chile", "Argentina"],
      blocks: [
        {
          title: "Estados Unidos",
          image: "/Dallas_Skyline.jpg",
          imagePosition: "center",
          description:
            "Trabajamos con empresas americanas que operan equipos, recursos o proveedores en Latinoamérica. Construyendo sistemas que funcionan fluidamente entre ambas culturas, sin fricción operativa. También guiamos a firmas norteamericanas que entran a mercados latinoamericanos con precisión.",
        },
        {
          title: "Latinoamérica",
          image: "/Panama_Skyline.jpg",
          imagePosition: "center 35%",
          description:
            "Nos asociamos con founders y ejecutivos de LatAm listos para expandirse a Estados Unidos. En fintech, energy tech, blockchain, marketing digital y más. Líderes que necesitan más que un abogado o un contador: un socio estratégico que conoce el camino, porque ya lo recorrió.",
        },
      ],
    },
    process: {
      eyebrow: "Cómo Funciona",
      headline1: "Un proceso construido",
      headline2: "sobre el",
      headlineItalic: "respeto.",
      stepLabel: (n) => `Paso ${n}`,
      steps: [
        {
          title: "Consulta Confidencial",
          description:
            "Envías una consulta breve describiendo tu organización y el desafío que estás enfrentando. Sin formularios largos. Sin secuencias automatizadas. Revisada personalmente en menos de 48 horas.",
        },
        {
          title: "Evaluación del Socio",
          description:
            "Un socio senior realiza una llamada de diagnóstico de 60 minutos. Evaluamos fit, alcance y urgencia. Rechazamos engagements en los que no podamos generar un impacto real. Esto protege tu tiempo y nuestra reputación.",
        },
        {
          title: "Diseño del Engagement",
          description:
            "Si hay fit mutuo, presentamos una propuesta a medida. Resultados específicos, metodología, timeline e inversión. Nada de decks plantilla.",
        },
        {
          title: "Engagement Intensivo",
          description:
            "El trabajo comienza de inmediato. Tu socio líder queda embebido con tu equipo directivo, presencial o virtualmente, durante toda la duración del mandato.",
        },
      ],
    },
    finalCTA: {
      eyebrow: "Inicia la Conversación",
      headline1: "El asesor correcto",
      headline2: "lo cambia",
      headlineItalic: "todo.",
      paragraph:
        "FWDCO Group acepta un número limitado de clientes nuevos cada trimestre. Ya sea que te estés expandiendo a Estados Unidos o fortaleciendo tu operación en LatAm. Si estás navegando un punto de inflexión crítico, queremos conocerte.",
      cta: "Solicitar Assessment",
    },
    contact: {
      eyebrow: "Envía tu Consulta",
      headline: "Solicitar Assessment",
      subtitle:
        "Revisada personalmente por el socio fundador. Respuesta en 48 horas.",
      highlights: [
        "NDA firmado antes de cualquier briefing estratégico",
        "Sin asociados junior. Sólo el socio fundador",
        "Respuesta personal dentro de 48 horas",
        "Estructuras de fee vinculadas a resultados disponibles",
        "Cero ruido. Cero frameworks de plantilla.",
      ],
      form: {
        name: "Nombre Completo",
        email: "Correo Electrónico",
        organization: "Organización",
        revenue: "Ingresos de tu organización",
        challenge:
          "Desafío principal (por favor comparte al menos una oración. Mínimo 10 caracteres)",
        submit: "Solicitar Assessment",
        revenueRanges: [
          { value: "under_1m", label: "Menos de $1M" },
          { value: "1m_5m", label: "$1M – $5M" },
          { value: "5m_25m", label: "$5M – $25M" },
          { value: "25m_100m", label: "$25M – $100M" },
          { value: "100m_plus", label: "$100M+" },
        ],
      },
      errors: {
        required: "Por favor completa todos los campos",
        shortName: "Por favor ingresa tu nombre completo (al menos 2 caracteres).",
        shortOrg: "Por favor ingresa el nombre de tu organización (al menos 2 caracteres).",
        shortChallenge:
          "Por favor describe tu desafío con un poco más de detalle (al menos 10 caracteres).",
        generic: "Algo salió mal. Por favor intenta de nuevo.",
        fieldError: (field, msg) => `Por favor revisa el campo "${field}": ${msg}.`,
      },
      success:
        "Tu consulta ha sido enviada. Responderemos en las próximas 48 horas.",
      receivedTitle: "Consulta Recibida",
      receivedDesc:
        "Revisada personalmente por el socio fundador. Respuesta en 48 horas.",
      submitAnother: "Enviar Otra Consulta",
      directEmailLead: "O escríbenos directamente a",
      directEmail: "info@fwdcogroup.com",
      directEmailTail:
        ". Respondemos en menos de 48 horas. Sin formularios y sin automatizaciones.",
    },
    footer: {
      tagline: "Consejo estratégico para líderes que construyen a través de las Américas.",
      links: [
        { label: "Nuestra Práctica", href: "#about" },
        { label: "Servicios", href: "#pricing" },
        { label: "Socios", href: "#network" },
        { label: "Proceso", href: "#process" },
        { label: "Contacto", href: "#contact" },
      ],
      copyright: (year) => `© ${year} FWDCO Group`,
    },
    newsletter: {
      title: "Inteligencia Curada. No Para Todos.",
      description:
        "Nuestro newsletter no es abierto. Revisamos cada solicitud y seleccionamos a quienes creemos que encontrarán valor real. Recursos, frameworks y análisis que no publicamos en ningún otro canal.",
      emailPlaceholder: "Tu email profesional",
      submit: "Solicitar Acceso",
      submitting: "Enviando…",
      success: "Solicitud recibida. Si hay fit, te escribimos.",
      successTitle: "Solicitud Recibida",
      successDesc:
        "Revisamos cada solicitud. Si hay fit, te escribimos en menos de 48 horas.",
      close: "Cerrar",
      maybeLater: "Revisamos cada solicitud. Si hay fit, te escribimos.",
      invalidEmail: "Por favor ingresa un correo electrónico válido.",
      genericError: "Algo salió mal. Por favor intenta de nuevo.",
    },
  },
};
